import { Text, Icon, Divider, Flex, Box, Button, Badge, useDisclosure } from '@chakra-ui/react';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from 'react-map-gl';

import { IconLocationPin, IconBriefcase, IconFileClock, IconClock, IconBreakTime } from 'assets/icons';
import CustomFieldModal from 'components/CustomFieldModal';
import { getRoundUpTime } from 'containers/EmployeeTimeClock/shared/helper';
import { useSearchParamStore } from 'containers/WhoIsWorking/shared/useSearchParamStore';
import useTimeDuration from 'containers/WhoIsWorking/shared/useTimeDuration';
import { ICustomFieldItem } from 'hooks/customField/useFetchPaginatedCustomFields';
import { useClockOut } from 'hooks/timeClock/userClockOut';
import { useFetchWhoIsWorking } from 'hooks/whoIsWorking/useFetchWhoIsWorking';
import { useCompanyInfo } from 'store/useUserStore';
import { transformCustomFieldsToPostData } from 'utils/customFieldsUtils';
import { formatMonthDayTime, formatDistance } from 'utils/format';
import { getTimezoneOffsetInHM } from 'utils/helpers';

interface IEndLocationPopupProps {
  item: any;
  activeEmployee?: any;
  activeEndLocationPopup: any;
  setActiveEndLocationPopup: (value: any) => void;
}

const EndLocationPopup: React.FC<IEndLocationPopupProps> = ({
  activeEndLocationPopup,
  setActiveEndLocationPopup,
  activeEmployee,
  item,
}) => {
  const { t } = useTranslation(['whoIsWorking', 'common', 'timesheetMileage']);
  const companyInfo = useCompanyInfo();

  const { working, group_ids } = useSearchParamStore();
  const { refetch: refetchWorkingEmployee } = useFetchWhoIsWorking({ working, group_ids }, { enabled: false });
  const { status: clockOutStatus, mutateAsync } = useClockOut();

  const { isOpen: isClockOutModalOpen, onOpen: onClockOutModalOpen, onClose: onClockOutModalClose } = useDisclosure();

  const timeDuration = useTimeDuration(item);

  const endLocationTime = useMemo(
    () =>
      item?.user_id === activeEmployee?.user_id && activeEmployee?.geo_locations.length
        ? activeEmployee.geo_locations[activeEmployee.geo_locations.length - 1]
        : undefined,
    [activeEmployee, item]
  );

  const handleClockOut = (customFieldsData: Array<ICustomFieldItem> | null, values: Record<string, any>) => {
    let latLong = {
      latitude: item?.start_latitude,
      longitude: item?.start_longitude,
    };

    const geoLocations = activeEmployee?.geo_locations;

    if (Array.isArray(geoLocations) && geoLocations.length) {
      const { latitude, longitude } = geoLocations[geoLocations.length - 1];
      latLong = { latitude, longitude };
    }
    const postData = {
      ...latLong,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: getTimezoneOffsetInHM(),
      clock_out_time: getRoundUpTime('clock_out'),
      user_id: item?.user_id,
      ...(customFieldsData ? { custom_fields: transformCustomFieldsToPostData(customFieldsData, values) } : {}),
    };

    mutateAsync(postData).then(() => {
      onClockOutModalClose();
      refetchWorkingEmployee();
    });
  };

  return (
    <>
      <CustomFieldModal
        type="clock_out"
        isOpen={isClockOutModalOpen}
        onClose={onClockOutModalClose}
        title={t('timesheetMileage:bulkClockOutModalTitle', {
          user: 'this user',
        })}
        subTitle={t('timesheetMileage:bulkClockOutModalSubTitle', {
          user: 'this user',
        })}
        jobId={item?.jobId}
        isConfirmButtonLoading={clockOutStatus === 'loading'}
        handleConfirmButtonClick={handleClockOut}
      />
      <Popup
        latitude={activeEndLocationPopup?.latitude}
        longitude={activeEndLocationPopup?.longitude}
        closeButton={true}
        closeOnClick={false}
        onClose={() => setActiveEndLocationPopup(null)}
        anchor="top"
        className="mapbox-popup-wrapper"
      >
        <Text fontSize="md">
          {item?.first_name} {item?.last_name}
        </Text>
        {item?.clock_in_address ? (
          <>
            <Text color="gray.500" fontSize="xs">
              <Icon as={IconLocationPin} fontSize="xs" mr="1" />
              {item?.clock_in_address}
            </Text>
            <Divider borderStyle="dashed" my="3" />
          </>
        ) : null}
        {item?.job_name ? (
          <Text fontSize="xs" color="gray.500">
            <Icon as={IconBriefcase} fontSize="xs" mr="2" sx={{ '& path': { fill: 'gray.400' } }} />
            {item?.job_name}
          </Text>
        ) : null}
        {item?.task_name ? (
          <Text fontSize="xs" color="gray.500">
            <Icon as={IconFileClock} fontSize="xs" mr="2" />
            {item?.task_name}
          </Text>
        ) : null}
        {item?.current_break ? (
          <div>
            <Icon as={IconBreakTime} fontSize="xs" sx={{ '& path': { fill: 'gray.400' } }} mr="2" />
            <Badge px="2" textTransform="lowercase" borderRadius="base" colorScheme="primary" color="gray.500">
              {item?.current_break_name || 'Break'}
            </Badge>
          </div>
        ) : null}
        <Divider borderStyle="dashed" my="3" />
        <Text fontSize="xs" color="gray.500">
          <Icon as={IconClock} fontSize="xs" mr="2" />
          {endLocationTime?.location_time ? (
            <>
              {t('common:at')} {formatMonthDayTime(endLocationTime.location_time)}
            </>
          ) : (
            <>
              {t('common:clockedInOn')} {item?.clock_in_time ? formatMonthDayTime(item?.clock_in_time) : '- '}
            </>
          )}
        </Text>

        <Flex justifyContent="space-evenly" textAlign="center" mt="3">
          <Box>
            <Text fontSize="md">{timeDuration}</Text>
            <Text fontSize="xs" color="gray.500">
              {t('common:duration')}
            </Text>
          </Box>
          <Box>
            <Text fontSize="md">{item?.distance ? formatDistance(item?.distance) : '- '}</Text>
            <Text fontSize="xs" color="gray.500">
              {t('common:distanceTravelled')}
            </Text>
          </Box>
        </Flex>
        {!companyInfo?.blockWebsitePunch ? (
          <Button
            bg="rgba(250, 85, 85, 0.1)"
            color="secondary.700"
            fontWeight="semibold"
            _hover={{ bg: 'rgba(250, 85, 85, 0.2)' }}
            _active={{ bg: 'rgba(250, 85, 85, 0.2)' }}
            size="sm"
            width="100%"
            mt="3"
            onClick={onClockOutModalOpen}
          >
            {t('common:clockOut')}
          </Button>
        ) : null}
      </Popup>
    </>
  );
};

export default memo(EndLocationPopup);
